import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Career } from 'src/app/interfaces/career';
import { Inscription } from 'src/app/interfaces/inscription';
import { Student } from 'src/app/interfaces/student';
import { ErrorService } from 'src/app/services/error.service';
import { CareerService } from 'src/app/services/career.service';
import { InscriptionService } from 'src/app/services/inscription.service';
import { StudentRegistrationPayload, StudentService } from 'src/app/services/student.service';
import { UserService } from 'src/app/services/user.service';

@Component({
  selector: 'app-add-edit-student',
  templateUrl: './add-edit-student.component.html',
  styleUrls: ['./add-edit-student.component.css']
})
export class AddEditStudentComponent implements OnInit {
  form: FormGroup;
  loading: boolean = false;
  id: number;
  operacion: string = 'Agregar';
  careers: Career[] = [];
  currentStudent?: Student;
  currentInscription?: Inscription;

  constructor(
    private fb: FormBuilder,
    private _studentService: StudentService,
    private _careerService: CareerService,
    private _inscriptionService: InscriptionService,
    private _userService: UserService,
    private router: Router,
    private toastr: ToastrService,
    private aRouter: ActivatedRoute,
    private _errorService: ErrorService
  ) {
    this.form = this.fb.group({
      name: ['', Validators.required],
      lastname: ['', Validators.required],
      ci: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      idcareer: ['', Validators.required]
    });
    this.id = Number(aRouter.snapshot.paramMap.get('id'));
  }

  ngOnInit(): void {
    this.loadCareers();
    if (this.id !== 0) {
      this.operacion = 'Editar';
      this.loadStudentRegistration(this.id);
    }
  }

  addStudent() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const payload: StudentRegistrationPayload = {
      name: this.form.value.name.trim(),
      lastname: this.form.value.lastname.trim(),
      ci: this.form.value.ci.trim(),
      email: this.form.value.email.trim(),
      idcareer: Number(this.form.value.idcareer),
      status: 1
    };

    if (this.id === 0) {
      payload.password = this.generatePassword();
      this.createStudent(payload);
      return;
    }

    this.updateStudent(payload);
  }

  private loadCareers() {
    this._careerService.getListCareers().subscribe({
      next: (careers) => {
        this.careers = careers;
      },
      error: (error) => {
        console.error('Error al obtener carreras:', error);
        this.toastr.error('No se pudieron cargar las carreras', 'Error');
      }
    });
  }

  private loadStudentRegistration(id: number) {
    this.loading = true;
    this._studentService.getStudent(id).subscribe({
      next: (student) => {
        this.currentStudent = student;
        this.form.patchValue({
          name: student.name,
          lastname: student.lastname,
          ci: student.ci
        });

        this.loadStudentEmail(student);
        this.loadStudentCareer(student.id!);
      },
      error: (error) => {
        console.error('Error al obtener el estudiante:', error);
        this.toastr.error('No se pudo cargar el estudiante', 'Error');
        this.loading = false;
      }
    });
  }

  private loadStudentEmail(student: Student) {
    if (!student.userid) {
      return;
    }

    this._userService.getUser(student.userid).subscribe({
      next: (user) => {
        this.form.patchValue({
          email: user?.username || ''
        });
      },
      error: (error) => {
        console.error('Error al obtener el usuario del estudiante:', error);
      }
    });
  }

  private loadStudentCareer(studentId: number) {
    this._inscriptionService.getInscriptionByStudentId(studentId).subscribe({
      next: (inscription) => {
        this.currentInscription = inscription;
        this.form.patchValue({
          idcareer: inscription.idcareer
        });
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  private createStudent(payload: StudentRegistrationPayload) {
    this.loading = true;
    this._studentService.registerStudentWithCareer(payload).subscribe({
      next: () => {
        this.loading = false;
        this.toastr.success(
          `El estudiante fue registrado con éxito. Contraseña inicial: ${payload.password}`,
          'Estudiante registrado'
        );
        this.router.navigate(['/student']);
      },
      error: (e: HttpErrorResponse) => {
        this.loading = false;
        this._errorService.msjError(e);
      }
    });
  }

  private updateStudent(payload: StudentRegistrationPayload) {
    this.loading = true;
    this._studentService.updateStudentRegistration(this.id, payload).subscribe({
      next: () => {
        this.loading = false;
        this.toastr.info('El estudiante y su carrera fueron actualizados con éxito', 'Estudiante actualizado');
        this.router.navigate(['/student']);
      },
      error: (e: HttpErrorResponse) => {
        this.loading = false;
        this._errorService.msjError(e);
      }
    });
  }

  private generatePassword(): string {
    return Math.floor(1000 + Math.random() * 9000).toString();
  }
}
